import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Shirt, Flame, Check } from "lucide-react";

export default function ShopView({ ch, setCh }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  // §SHOP-BG: same class-based backdrop pipeline as `.market-bg-active`.
  // Body paints the merchant panorama fullscreen; game-root drops its
  // inline gradient so the image shows through the 1024px column.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.classList.add("shop-bg-active");
    const root = document.querySelector('[data-testid="game-root"]');
    const prev = root ? { bg: root.style.background, bc: root.style.backgroundColor } : null;
    if (root) {
      root.style.background = "transparent";
      root.style.backgroundColor = "transparent";
    }
    return () => {
      document.body.classList.remove("shop-bg-active");
      if (root && prev) {
        root.style.background = prev.bg;
        root.style.backgroundColor = prev.bc;
      }
    };
  }, []);

  const refresh = async () => setData(await api.cosmetics());
  useEffect(() => { refresh(); }, []);

  const buy = async (id) => {
    setBusy(true);
    try {
      const updated = await api.cosmeticsBuy(id);
      setCh(updated);
      await refresh();
      toast.success(t("shop.purchased"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
    finally { setBusy(false); }
  };

  const equip = async (id) => {
    setBusy(true);
    try {
      const updated = await api.cosmeticsEquip(id);
      setCh(updated);
      await refresh();
      toast.success(t("shop.equipped"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="panel p-4 text-center text-[#666] text-sm italic">{t("shop.loading")}</div>;

  const Section = ({ title, items, Icon, slot }) => (
    <div className="panel p-2" data-testid={slot === "cloak" ? "shop-cloaks" : "shop-torches"}>
      <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] px-2 py-1 flex items-center gap-2">
        <Icon className="w-3 h-3 text-[#E55A00]" strokeWidth={1.5} /> {title}
      </div>
      <div className="grid grid-cols-2 gap-1">
        {items.map(c => {
          const owned = data.owned.includes(c.id);
          const equipped = data.equipped?.[slot] === c.id;
          return (
            <div key={c.id} className="panel-inner p-2 flex flex-col gap-1" data-testid={`shop-item-${c.id}`}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 border border-[#333]" style={{ background: c.color }} />
                <div className="min-w-0">
                  <div className="font-heading text-xs text-[#E2DFD8] truncate">{c.name}</div>
                  <div className="font-mono text-[10px] text-[#E5C34A]">{c.price}g</div>
                </div>
              </div>
              {!owned && (
                <button
                  data-testid={`shop-buy-${c.id}`}
                  onClick={() => buy(c.id)}
                  disabled={busy || ch.gold < c.price}
                  className="w-full py-1 border border-[#B89C3A] text-[#E5C34A] font-heading text-[10px] tracking-widest uppercase disabled:opacity-30"
                >{t("shop.buy")}</button>
              )}
              {owned && !equipped && (
                <button
                  data-testid={`shop-equip-${c.id}`}
                  onClick={() => equip(c.id)}
                  disabled={busy}
                  className="w-full py-1 border border-[#333] text-[#8c8c8c] hover:border-[#E55A00] hover:text-[#E55A00] font-heading text-[10px] tracking-widest uppercase"
                >{t("shop.equip")}</button>
              )}
              {equipped && (
                <div className="w-full py-1 border border-[#4A5D23] text-[#6E8B3D] text-center font-heading text-[10px] tracking-widest uppercase flex items-center justify-center gap-1">
                  <Check className="w-3 h-3" strokeWidth={2} /> {t("shop.worn")}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="space-y-2">
      <div className="panel p-3">
        <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c]">{t("shop.title")}</div>
        <p className="italic text-[10px] text-[#666] mt-1">{t("shop.hint")}</p>
      </div>
      <Section title={t("shop.cloaks")} items={data.catalog.cloaks} Icon={Shirt} slot="cloak" />
      <Section title={t("shop.torches")} items={data.catalog.torches} Icon={Flame} slot="torch" />
    </div>
  );
}
